/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { DateTime } from 'luxon'
import { Group, Input, InputWrapper, InputWrapperProps, TextInputProps } from '@mantine/core'

export type DateTimeInputProps = InputWrapperProps
  & Omit<TextInputProps, 'onChange' | 'value'>
  & {
  hideTime?: boolean,
  onChange? (value?: string): void,
  suppressMilliseconds?: boolean,
  suppressSeconds?: boolean,
  value?: string,
}

function DateTimeInput (props: DateTimeInputProps) {
  const {
    disabled,
    description,
    error,
    hideTime,
    label,
    // todo support max and min constraints
    // max,
    // min,
    name,
    onChange,
    required,
    suppressMilliseconds = false,
    suppressSeconds = false,
    value,
    wrapperProps
  } = props

  const [dateString, setDateString] = useState<string>()
  const [timeString, setTimeString] = useState<string>()

  const normalizedValue = useMemo(() => {
    if (value != null && value !== '') {
      let dt = DateTime.fromISO(value)

      if (suppressMilliseconds) {
        dt = dt.set({ millisecond: 0 })
      }
      if (suppressSeconds) {
        dt = dt.set({ second: 0 })
      }
      return dt.isValid
        ? dt.toISO({ suppressMilliseconds, suppressSeconds })
        : value
    }
    return value
  }, [suppressMilliseconds, suppressSeconds, value])

  const handleDateChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const dateValue = event.target.value
    if (dateValue === '') {
      setDateString(null)
      setTimeString(null)
      onChange(null)
      return
    }
    setDateString(dateValue)

    const dt = DateTime.fromISO(dateValue + 'T' + (timeString || '00:00'), {
      zone: DateTime.local().zone
    })
    if (dt.isValid && dt.year >= 1900) {
      onChange(dt.toISO())
    }
  }, [onChange, timeString])

  const handleTimeChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const timeValue = event.target.value
    if (timeValue === '') {
      setDateString(null)
      setTimeString(null)
      onChange(null)
      return
    }
    setTimeString(timeValue)

    const dt = DateTime.fromISO(
      dateString + 'T' + timeValue,
      { zone: DateTime.local().zone })

    if (dt.isValid && dt.year >= 1900) {
      onChange(dt.toISO())
    }
  }, [dateString, onChange])

  useEffect(() => {
    if (normalizedValue != null && normalizedValue !== '') {
      const dt = DateTime.fromISO(normalizedValue)
      if (dt.isValid) {
        setDateString(dt.toISODate())
        setTimeString(dt.toISOTime({
          includeOffset: false,
          includePrefix: false,
          suppressMilliseconds,
          suppressSeconds
        }))
      }
    } else {
      setDateString(null)
      setTimeString(null)
    }
  }, [normalizedValue, suppressMilliseconds, suppressSeconds])

  const timePattern = useMemo(() => {
    if (suppressSeconds) {
      return '[0-9]{2}:[0-9]{2}'
    }
    if (suppressMilliseconds) {
      return '[0-9]{2}:[0-9]{2}(:[0-9]{2})?'
    }
    return '[0-9]{2}:[0-9]{2}(:[0-9]{2})?(.[0-9]{3})?'
  }, [suppressMilliseconds, suppressSeconds])

  const timePlaceholder = useMemo(() => {
    if (suppressSeconds) {
      return 'hh:mm'
    }
    if (suppressMilliseconds) {
      return 'hh:mm:ss'
    }
    return 'hh:mm:ss.SSS'
  }, [suppressMilliseconds, suppressSeconds])

  return (
    <InputWrapper
      description={description}
      error={error}
      label={label}
      required={required}
      {...wrapperProps}
    >
      <input
        type="hidden"
        disabled={disabled}
        name={name}
        value={normalizedValue != null ? normalizedValue : ''}
      />
      <Group grow>
        <Input
          disabled={disabled}
          onChange={handleDateChange}
          pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}"
          placeholder="yyyy-mm-dd"
          required={required}
          type="date"
          value={dateString != null ? dateString : ''}
        />
        {!hideTime
          ? <Input
            disabled={disabled || normalizedValue == null || normalizedValue === ''}
            onChange={handleTimeChange}
            pattern={timePattern}
            placeholder={timePlaceholder}
            required={required}
            type="time"
            value={timeString != null ? timeString : ''}
          />
          : null}
      </Group>
    </InputWrapper>
  )
}

export default DateTimeInput
